import { useTranslation } from 'react-i18next'
import type { NavigateOptions, To } from 'react-router-dom'
import type { Alpha3Code } from 'i18n-iso-countries'

import { SuggestedRow } from '../SuggestedRow/SuggestedRow'
import suggestedRowClasses from '../SuggestedRow/SuggestedRow.module.scss'

import { Box } from '@percent/lemonade'
import { useGivingActionInteractions } from '@percent/workplace-giving/common/components/GivingActionCard/useGivingActionInteractions'
import { InfiniteScrollingCarousel } from '@percent/workplace-giving/common/components/InfiniteScrollingCarousel/InfiniteScrollingCarousel'
import { GivingActionCardSkeleton } from '@percent/workplace-giving/common/components/GivingActionCard/GivingActionCardSkeleton'
import classes from './GivingActionsRow.module.scss'
import { useGivingActionsRow } from './useGivingActionsRow'

export type GivingActionsRowProps = Readonly<{
  campaignId?: string
  title?: string
  tilesAmount?: number
  targetCountries?: Alpha3Code[]
  seeAllHref?: To
  seeAllNavigateOptions?: NavigateOptions
  layout?: 'grid' | 'carousel'
  includeUnpublished?: boolean
}>

export function GivingActionsRow({
  campaignId,
  title: titleOverride,
  tilesAmount: tilesAmountOverride,
  targetCountries,
  seeAllHref,
  seeAllNavigateOptions,
  layout = 'grid',
  includeUnpublished
}: GivingActionsRowProps = {}): JSX.Element | null {
  const { t } = useTranslation()
  const { givingActions, skeletonData, tilesAmount, isPending, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGivingActionsRow({ campaignId, tilesAmount: tilesAmountOverride, targetCountries, includeUnpublished })
  const title = titleOverride ?? t('workplace_giving.givingActions.title')
  const { renderCard, overlays } = useGivingActionInteractions({ showCampaignName: !campaignId })

  if (isPending) {
    return (
      <SuggestedRow
        title={title}
        testId="giving-actions-row-skeleton"
        items={skeletonData}
        keyExtractor={key => key}
        renderItem={() => <GivingActionCardSkeleton />}
      />
    )
  }

  if (givingActions.length === 0) {
    return null
  }

  if (layout === 'carousel') {
    return (
      <>
        <Box className={suggestedRowClasses.SuggestedRowContainer} data-testid="giving-actions-row">
          <InfiniteScrollingCarousel
            title={title}
            titleClassName={suggestedRowClasses.SuggestedRowTitle}
            headerClassName={suggestedRowClasses.SuggestedRowHeader}
            navWrapperClassName={classes.NavWrapper}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            data={givingActions}
            itemsPerPage={tilesAmount}
            keyExtractor={row => row.id}
            renderItem={renderCard}
          />
        </Box>
        {overlays}
      </>
    )
  }

  return (
    <>
      <SuggestedRow
        title={title}
        testId="giving-actions-row"
        items={givingActions.slice(0, tilesAmount)}
        keyExtractor={row => row.id}
        renderItem={renderCard}
        seeAllHref={givingActions.length >= tilesAmount ? seeAllHref : undefined}
        seeAllNavigateOptions={seeAllNavigateOptions}
      />
      {overlays}
    </>
  )
}
